/**
 * 战略Dashboard主页面
 * 集成三层指标体系的综合战略视图
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, RefreshCw, Settings, Download } from 'lucide-react';
import { clsx } from 'clsx';

import ProductHealthView from '../components/views/ProductHealthView';
import AiPerformanceView from '../components/views/AiPerformanceView';
import EcosystemView from '../components/views/EcosystemView';
import { fetchStrategyDashboard, fetchDashboardAlerts, exportDashboardData } from '../services/api';

// Using any for now to fix TypeScript compilation
type StrategyDashboardData = any;
type DashboardConfig = any;
type DashboardAlert = any;

type ViewType = 'overview' | 'product-health' | 'ai-performance' | 'ecosystem';

const StrategyDashboard: React.FC = () => {
  const [data, setData] = useState<StrategyDashboardData | null>(null);
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [autoRefresh] = useState(true);
  const [config] = useState<DashboardConfig>({
    refreshInterval: 300, // 5分钟
    timeGranularity: 'day',
    enableRealTimeUpdates: true,
    alertThresholds: {
      lowCoreActivationRate: 0.3,
      highManualCorrectionEffort: 1000,
      lowAiAccuracy: 0.7,
      slowDataFlywheelVelocity: 0.2
    }
  });

  // 获取Dashboard数据
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 获取综合战略数据
      const data = await fetchStrategyDashboard();
      setData(data);
      setLastUpdated(data.lastUpdated);

      // 获取告警
      try {
        const alertsData = await fetchDashboardAlerts();
        setAlerts(alertsData);
      } catch (alertError) {
        console.warn('获取告警数据失败:', alertError);
        // 告警失败不影响主要数据显示
      }

    } catch (err) {
      console.error('获取Dashboard数据失败:', err);
      setError(err instanceof Error ? err.message : '获取数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化数据加载
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // 自动刷新
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDashboardData();
    }, config.refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, config.refreshInterval, fetchDashboardData]);

  // 导出数据
  const handleExportData = async () => {
    if (!data) return;

    try {
      const blob = await exportDashboardData({
        format: 'csv',
        timeRange: data.productHealth.timeRange
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('导出失败:', error);
    }
  };

  // 标签页配置
  const tabs = [
    { id: 'overview', name: '综合概览', description: '整体战略指标概览' },
    { id: 'product-health', name: '产品健康度', description: '用户参与与价值激活' },
    { id: 'ai-performance', name: 'AI性能', description: 'AI核心性能与用户体验' },
    { id: 'ecosystem', name: '生态系统', description: '数据护城河与网络效应' },
  ];

  // 数据质量指示器
  const DataQualityIndicator: React.FC<{ quality: any }> = ({ quality }) => (
    <div className="flex items-center space-x-4 text-sm text-gray-600">
      <div className="flex items-center">
        <div className="w-2 h-2 rounded-full bg-green-500 mr-1" />
        <span>完整性 {(quality.completeness * 100).toFixed(0)}%</span>
      </div>
      <div className="flex items-center">
        <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" />
        <span>新鲜度 {quality.freshness.toFixed(1)}h</span>
      </div>
      <div className="flex items-center">
        <div className="w-2 h-2 rounded-full bg-purple-500 mr-1" />
        <span>准确性 {(quality.accuracy * 100).toFixed(0)}%</span>
      </div>
    </div>
  );

  // 告警面板
  const AlertPanel: React.FC = () => {
    const criticalAlerts = alerts.filter(a => a.type === 'critical' && !a.acknowledged);
    const warningAlerts = alerts.filter(a => a.type === 'warning' && !a.acknowledged);

    if (criticalAlerts.length === 0 && warningAlerts.length === 0) {
      return null;
    }

    return (
      <div className="mb-6">
        {criticalAlerts.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {criticalAlerts.length} 个关键告警
                </h3>
                <div className="mt-2 space-y-1">
                  {criticalAlerts.slice(0, 3).map((alert) => (
                    <p key={alert.id} className="text-sm text-red-700">
                      {alert.title}: {alert.message}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {warningAlerts.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {warningAlerts.length} 个警告
                </h3>
                <div className="mt-2 space-y-1">
                  {warningAlerts.slice(0, 2).map((alert) => (
                    <p key={alert.id} className="text-sm text-yellow-700">
                      {alert.title}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 综合概览视图
  const OverviewView: React.FC = () => {
    if (!data) return null;

    return (
      <div className="space-y-8">
        {/* 核心KPI */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {(data.productHealth.coreValueActivationRate * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">核心价值激活率</div>
            <div className={clsx(
              'text-xs mt-1',
              data.productHealth.coreValueActivationRate >= 0.5 ? 'text-green-600' :
              data.productHealth.coreValueActivationRate >= 0.3 ? 'text-yellow-600' : 'text-red-600'
            )}>
              {data.productHealth.coreValueActivationRate >= 0.5 ? '良好' :
               data.productHealth.coreValueActivationRate >= 0.3 ? '一般' : '需关注'}
            </div>
          </div>

          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {data.productHealth.practitionerEngagementScore.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">从业者参与度</div>
            <div className={clsx(
              'text-xs mt-1',
              data.productHealth.practitionerEngagementScore >= 1.5 ? 'text-green-600' :
              data.productHealth.practitionerEngagementScore >= 1.0 ? 'text-yellow-600' : 'text-red-600'
            )}>
              {data.productHealth.practitionerEngagementScore >= 1.5 ? '活跃' :
               data.productHealth.practitionerEngagementScore >= 1.0 ? '一般' : '偏低'}
            </div>
          </div>

          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {(data.aiPerformanceMetrics.accuracyRate * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">AI准确率</div>
            <div className={clsx(
              'text-xs mt-1',
              data.aiPerformanceMetrics.accuracyRate >= 0.85 ? 'text-green-600' :
              data.aiPerformanceMetrics.accuracyRate >= 0.7 ? 'text-yellow-600' : 'text-red-600'
            )}>
              {data.aiPerformanceMetrics.accuracyRate >= 0.85 ? '优秀' :
               data.aiPerformanceMetrics.accuracyRate >= 0.7 ? '良好' : '需优化'}
            </div>
          </div>

          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-indigo-600 mb-2">
              {data.ecosystem.dataFlywheelVelocityIndex.toFixed(3)}
            </div>
            <div className="text-sm text-gray-600">数据飞轮速度</div>
            <div className={clsx(
              'text-xs mt-1',
              data.ecosystem.dataFlywheelVelocityIndex >= 0.5 ? 'text-green-600' :
              data.ecosystem.dataFlywheelVelocityIndex >= 0.2 ? 'text-yellow-600' : 'text-red-600'
            )}>
              {data.ecosystem.dataFlywheelVelocityIndex >= 0.5 ? '快速' :
               data.ecosystem.dataFlywheelVelocityIndex >= 0.2 ? '稳定' : '缓慢'}
            </div>
          </div>
        </div>

        {/* 战略健康度总结 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">战略健康度总结</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">产品层面</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">用户激活</span>
                  <span className={data.productHealth.coreValueActivationRate >= 0.5 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.productHealth.coreValueActivationRate >= 0.5 ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">用户参与</span>
                  <span className={data.productHealth.practitionerEngagementScore >= 1.0 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.productHealth.practitionerEngagementScore >= 1.0 ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">AI依赖</span>
                  <span className={data.productHealth.aiDependencyRate >= 0.5 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.productHealth.aiDependencyRate >= 0.5 ? '✓' : '⚠'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">AI层面</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">模型准确性</span>
                  <span className={data.aiPerformanceMetrics.accuracyRate >= 0.85 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.aiPerformanceMetrics.accuracyRate >= 0.85 ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">用户体验</span>
                  <span className={data.aiPerformance.manualCorrectionEffortIndex <= 500 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.aiPerformance.manualCorrectionEffortIndex <= 500 ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">系统性能</span>
                  <span className={data.apiLatencyStats.p95Latency <= 3000 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.apiLatencyStats.p95Latency <= 3000 ? '✓' : '⚠'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">生态层面</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">增长速度</span>
                  <span className={data.ecosystem.details.weeklyActiveUserGrowthRate >= 0.05 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.ecosystem.details.weeklyActiveUserGrowthRate >= 0.05 ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">网络效应</span>
                  <span className={data.ecosystem.dataFlywheelVelocityIndex >= 0.5 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.ecosystem.dataFlywheelVelocityIndex >= 0.5 ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">生态多样性</span>
                  <span className={data.ecosystem.contributorConcentrationRatio <= 0.6 ? 'text-green-600' : 'text-yellow-600'}>
                    {data.ecosystem.contributorConcentrationRatio <= 0.6 ? '✓' : '⚠'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-2 text-gray-600">加载战略Dashboard数据...</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="btn-primary"
            disabled={loading}
          >
            {loading ? '重试中...' : '重试'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">战略Dashboard</h1>
          <p className="text-gray-600 mt-1">ACC AI助手系统战略指标监控</p>
        </div>

        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          {/* 数据质量指示器 */}
          {data && <DataQualityIndicator quality={data.dataQuality} />}
          
          {/* 操作按钮 */}
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="btn-secondary btn-sm"
              title="刷新数据"
            >
              <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
            </button>

            <button
              onClick={handleExportData}
              disabled={!data}
              className="btn-secondary btn-sm"
              title="导出数据"
            >
              <Download className="h-4 w-4" />
            </button>

            <button className="btn-secondary btn-sm" title="设置">
              <Settings className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 告警面板 */}
      <AlertPanel />

      {/* 标签页导航 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentView(tab.id as ViewType)}
              className={clsx(
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
                currentView === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <div>
                <div>{tab.name}</div>
                <div className="text-xs text-gray-400">{tab.description}</div>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* 内容区域 */}
      <div className="min-h-screen">
        {data && (
          <>
            {currentView === 'overview' && <OverviewView />}
            {currentView === 'product-health' && (
              <ProductHealthView
                productHealth={data.productHealth}
                userStats={data.userStats}
                funnelStats={data.funnelStats}
                timeSeries={{
                  dau: data.timeSeries.dau,
                  wau: data.timeSeries.wau,
                  mau: data.timeSeries.mau,
                  coreActivationRate: data.timeSeries.coreActivationRate,
                  practitionerEngagement: data.timeSeries.practitionerEngagement
                }}
                trends={{
                  dau: data.trends.dau,
                  coreActivationRate: data.trends.coreActivationRate,
                  practitionerEngagement: data.trends.practitionerEngagement
                }}
              />
            )}
            {currentView === 'ai-performance' && (
              <AiPerformanceView
                aiCorePerformance={data.aiPerformance}
                aiPerformance={data.aiPerformanceMetrics}
                apiLatency={data.apiLatencyStats}
                timeSeries={{
                  aiAccuracy: data.timeSeries.aiAccuracy,
                  manualCorrectionEffort: data.timeSeries.manualCorrectionEffort
                }}
                trends={{
                  aiAccuracy: data.trends.aiAccuracy
                }}
              />
            )}
            {currentView === 'ecosystem' && (
              <EcosystemView
                ecosystem={data.ecosystem}
                timeSeries={{
                  dataFlywheelVelocity: data.timeSeries.dataFlywheelVelocity
                }}
                trends={{
                  dataFlywheelVelocity: data.trends.dataFlywheelVelocity
                }}
              />
            )}
          </>
        )}
      </div>

      {/* 页脚信息 */}
      {lastUpdated && (
        <div className="text-center text-sm text-gray-500 py-4 border-t border-gray-200">
          最后更新: {new Date(lastUpdated).toLocaleString('zh-CN')}
          {autoRefresh && (
            <span className="ml-2">
              • 自动刷新: {config.refreshInterval}秒
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default StrategyDashboard;